export async function fetchOrError<T>(
  url: string,
  init?: { method: 'POST' | 'PATCH'; body: unknown } | { method: 'DELETE' },
): Promise<T> {
  const body = init
    ? {
      method: init.method,
      body: 'body' in init ? JSON.stringify(init.body) : undefined,
      headers: { 'content-type': 'application/json', 'accept': 'application/json' },
    }
    : undefined;

  const res = await fetch(url, body);
  if (!res.ok) {
    throw new Error(`Fetch failed: ${res.status} ${res.statusText}: ${await res.text()}`);
  }
  return await res.json();
}





declare global {
  interface ReadableStream<R> {
    [Symbol.asyncIterator](): AsyncIterableIterator<R>;
  }
}

declare global {
  interface ReadableStream<R> {
    [Symbol.asyncIterator](): AsyncIterableIterator<R>;
  }
}

/**
 * Fetches data from the given URL as a streaming response and returns an AsyncIterable
 * that yields chunks as strings. The response can be iterated over using `for await`.
 * 
 * @param url - The URL to fetch.
 * @param options - Optional fetch options (e.g., method, headers, body for POST).
 * @yields Yields string chunks from the response stream.
 * @throws If the fetch request fails or the response is not readable.
 * @example
 * ```typescript
 * // Example usage:
 * (async () => {
 *   for await (const chunk of fetchStream('https://api.example.com/stream', { method: 'POST', body: JSON.stringify({ prompt: 'Hello' }) })) {
 *     console.log(chunk);  // Process each chunk
 *   }
 * })();
 * ```
 */
export async function* fetchStream(url: string, options: RequestInit = {}): AsyncGenerator<string> {
  const response = await fetch(url, options);

  if (!response.ok) {
    throw new Error(`Fetch failed: ${response.status} ${response.statusText}`);
  }

  if (!response.body) {
    throw new Error('Response body is not a readable stream');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      // Yield the decoded string chunk
      yield decoder.decode(value, { stream: true });
    }
  } finally {
    reader.releaseLock();
  }
}